import { useTranslation } from "react-i18next";
import {
  Button,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
  Tile,
} from "@carbon/react";
import { CheckmarkFilled, Email, UserFollow } from "@carbon/icons-react";
import { SupplierRfqActivity, UserResponse } from "../../types";
import SupplierViewStatus from "./SupplierViewStatus";
import { activityByEmail } from "./SupplierValidationTag";
import "./bid.scss";

type SuppliersSelectedProps = {
  suppliers?: UserResponse[];
  invitedSuppliers?: UserResponse[];
  /** Per-supplier activity, matched to the rows by email; omitted where the viewer may not see it. */
  activity?: SupplierRfqActivity[];
  onAddSupplier?: () => void;
};


/**
 * The invited/selected suppliers panel (Task 7) — a Carbon `Tile` whose rows are now a real Carbon
 * `StructuredList` (replacing the hand-rolled bordered `<ul>`). `onAddSupplier` stays
 * optional/undefined-gated exactly as before: the caller (`BuyerBid.tsx`/`JointBuyerBid.tsx`) only
 * passes a handler while the order is still open for adding suppliers (OPEN/CONSULTATION and not a
 * requester) — see the C5 comment at each call site. Each row shows whether the supplier has opened
 * the RFQ when `activity` is passed.
 */
const SuppliersSelected = ({
  suppliers,
  invitedSuppliers,
  activity,
  onAddSupplier,
}: SuppliersSelectedProps) => {
  const { t } = useTranslation();
  const activityOfEmail = activityByEmail(activity);
  const activityOf = (email?: string) =>
    email ? activityOfEmail.get(email.toLowerCase()) : undefined;

  const hasSuppliers = Boolean(suppliers && suppliers.length > 0);
  const hasInvited = Boolean(invitedSuppliers && invitedSuppliers.length > 0);

  return (
    <Tile className="bid-tile">
      <div className="bid-suppliers__header">
        <h3 className="bid-tile__title bid-suppliers__title">
          {t("tenders.data.suppliers")}
        </h3>
        {onAddSupplier && (
          <Button
            kind="tertiary"
            size="sm"
            renderIcon={UserFollow}
            onClick={onAddSupplier}
          >
            {t("cta.addSupplier")}
          </Button>
        )}
      </div>

      {hasSuppliers && (
        <div className="bid-suppliers__section">
          <h4 className="bid-suppliers__section-title">
            {t("tenders.data.suppliers")}
          </h4>
          <StructuredListWrapper isCondensed className="bid-suppliers__list">
            <StructuredListBody>
              {suppliers?.map((sup) => (
                <StructuredListRow key={sup.id}>
                  <StructuredListCell className="bid-suppliers__icon-cell">
                    <CheckmarkFilled className="bid-suppliers__item-icon--selected" />
                  </StructuredListCell>
                  <StructuredListCell>
                    {sup.company?.name || sup.fullName || sup.email}
                    <SupplierViewStatus activity={activityOf(sup.email)} />
                  </StructuredListCell>
                </StructuredListRow>
              ))}
            </StructuredListBody>
          </StructuredListWrapper>
        </div>
      )}

      {hasInvited && (
        <div className="bid-suppliers__section">
          <h4 className="bid-suppliers__section-title">
            {t("tenders.data.invited")}
          </h4>
          <StructuredListWrapper isCondensed className="bid-suppliers__list">
            <StructuredListBody>
              {invitedSuppliers?.map((sup) => (
                <StructuredListRow key={sup.id}>
                  <StructuredListCell className="bid-suppliers__icon-cell">
                    <Email className="bid-suppliers__item-icon--invited" />
                  </StructuredListCell>
                  <StructuredListCell>
                    {sup.email}
                    <SupplierViewStatus activity={activityOf(sup.email)} />
                  </StructuredListCell>
                </StructuredListRow>
              ))}
            </StructuredListBody>
          </StructuredListWrapper>
        </div>
      )}

      {!hasSuppliers && !hasInvited && (
        <p className="bid-suppliers__empty">{t("suppliers.noSuppliersFound")}</p>
      )}
    </Tile>
  );
};

export default SuppliersSelected;
